import {
  BSB_DIRECTORY_SENTINEL_IDS,
  RUN_LEVEL_BSB_RULE_CODES,
  type BsbValidationDetails,
  type BsbValidationFinding,
} from './bsbValidationApiTypes';

/**
 * Banking (BSB) state as the Pay Run surface understands it. Derived purely from the PRR-309 contract — the UI never
 * recomputes severity, overridability or release eligibility.
 */
export type PayRunBankingState =
  | 'not_evaluated'
  | 'checking'
  | 'verified'
  | 'attention'
  | 'blocked'
  | 'unavailable';

export interface PayRunBankingSummary {
  state: PayRunBankingState;
  /** Findings that are blocking release. */
  blockedCount: number;
  /** Overridable findings that have not been overridden yet. */
  attentionCount: number;
  /** Findings already overridden; they stay visible for audit. */
  overriddenCount: number;
}

/** What the Pay Run detail screen hands to the timeline model. */
export interface PayRunBankingInput {
  details: BsbValidationDetails | null;
  loading: boolean;
  /** True only when the read failed for a reason other than "not yet evaluated". */
  failed: boolean;
}

const isRunLevel = (finding: BsbValidationFinding): boolean =>
  (RUN_LEVEL_BSB_RULE_CODES as readonly string[]).includes(finding.ruleCode);

/** Worker-level findings only. Run-level conditions are presented separately and never as a worker row. */
export const workerFindings = (details: BsbValidationDetails | null): BsbValidationFinding[] =>
  (details?.findings ?? []).filter((finding) => !isRunLevel(finding));

/** Run-level findings only: directory unavailable and stale directory. */
export const runLevelFindings = (details: BsbValidationDetails | null): BsbValidationFinding[] =>
  (details?.findings ?? []).filter(isRunLevel);

/**
 * True when the backend reported a real directory ingestion. The sentinel ids pair with a Unix-epoch completion
 * timestamp, which must never be rendered as a directory "as at" date.
 */
export const hasDirectoryEvidence = (details: BsbValidationDetails | null): boolean => {
  if (!details) return false;
  if ((BSB_DIRECTORY_SENTINEL_IDS as readonly string[]).includes(details.directoryIngestionId)) return false;
  const completedAt = Date.parse(details.directoryCompletedAt);
  return Number.isFinite(completedAt) && completedAt > 0;
};

export const summarisePayRunBanking = ({ details, loading, failed }: PayRunBankingInput): PayRunBankingSummary => {
  // Blocked and attention counts come from the contract's own summary, not from recounting the findings array
  // (plan decision D-009). Overridden has no contract counter, so it is derived from the findings themselves.
  const blockedCount = details?.summary.blockingCount ?? 0;
  const attentionCount = details?.summary.unresolvedOverrideCount ?? 0;
  const overriddenCount = (details?.findings ?? []).filter((finding) => finding.overridden).length;

  // Known evidence wins over a refresh in flight. Banking is re-read on every readiness poll and on window focus,
  // so treating any in-flight read as 'checking' would repeatedly blank the panel and withdraw the "Review banking
  // findings" action mid-interaction. Only a first load with nothing to show is 'checking'.
  if (!details) {
    if (loading) return { state: 'checking', blockedCount: 0, attentionCount: 0, overriddenCount: 0 };
    if (failed) return { state: 'unavailable', blockedCount: 0, attentionCount: 0, overriddenCount: 0 };
    return { state: 'not_evaluated', blockedCount: 0, attentionCount: 0, overriddenCount: 0 };
  }
  if (blockedCount > 0) return { state: 'blocked', blockedCount, attentionCount, overriddenCount };
  if (attentionCount > 0) return { state: 'attention', blockedCount, attentionCount, overriddenCount };
  return { state: 'verified', blockedCount, attentionCount, overriddenCount };
};

/** True while Banking is the reason the Review stage cannot progress. */
export const bankingNeedsAttention = (summary: PayRunBankingSummary): boolean =>
  summary.state === 'blocked' || summary.state === 'attention';

/** Short sub-step label for the Review stage. Always text, never colour or icon alone. */
export const bankingSubStepLabel = (summary: PayRunBankingSummary): string => {
  switch (summary.state) {
    case 'checking': return 'Banking (BSB): checking';
    case 'verified': return 'Banking (BSB): verified';
    case 'blocked': return `Banking: ${summary.blockedCount} blocked`;
    case 'attention': return `Banking: ${summary.attentionCount} to review`;
    case 'unavailable': return 'Banking (BSB): unavailable';
    default: return 'Banking (BSB): not evaluated';
  }
};

/**
 * Short Review-stage detail text, or null when Banking should not replace the stage's normal detail. Healthy,
 * checking and not-yet-evaluated Banking stays low-noise.
 */
export const bankingStageDetail = (summary: PayRunBankingSummary): string | null => {
  if (summary.state === 'blocked') return `Banking: ${summary.blockedCount} blocked`;
  if (summary.state === 'attention') return `Banking: ${summary.attentionCount} to review`;
  return null;
};

/** Human-readable label for a finding's rule code. */
export const bankingFindingLabel = (ruleCode: string): string => {
  switch (ruleCode) {
    case 'WithdrawnBsb': return 'Withdrawn BSB';
    case 'SupersededBsb': return 'Merged or superseded BSB';
    case 'NonElectronicBsb': return 'Non-electronic BSB';
    case 'UnknownBsb': return 'Unknown BSB';
    case 'StaleBsbDirectory': return 'Stale BSB directory';
    case 'BsbDirectoryUnavailable': return 'BSB directory unavailable';
    default: return ruleCode;
  }
};

/** Operator guidance for findings the backend will never allow to be overridden. */
export const bankingFindingGuidance = (finding: BsbValidationFinding): string | null => {
  if (finding.overrideAllowed || finding.overridden) return null;
  if (finding.ruleCode === 'WithdrawnBsb')
    return 'This BSB has been withdrawn. Correct the source banking data for this worker, then revalidate.';
  if (finding.ruleCode === 'UnknownBsb')
    return 'This BSB was not found in the directory. Correct the source banking data for this worker, then revalidate.';
  return null;
};

/** Status shown in the findings table. Reflects the contract; it is not recomputed. */
export const bankingFindingStatus = (finding: BsbValidationFinding): 'Overridden' | 'Needs review' | 'Blocking' => {
  if (finding.overridden) return 'Overridden';
  if (finding.severity === 'blocker') return 'Blocking';
  return 'Needs review';
};
